import { HttpClient, HttpParams } from '@angular/common/http';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { inject, Injectable } from '@angular/core';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { Observable } from 'rxjs';
import { UnitConversionService } from '@app/core/services/custom-services/unit-conversion.service';
import { environment } from '@environment/environment';
import { _ } from '@ngx-translate/core';
import { IProdOrderBatchDTO } from '@app/core/models/prod-order-batch.model';
import { IProdOrderDTO, IProdOrderStateDTO, IProdOrderLineDTO, IProdOrderDeleteResponseDTO, ProductActivityDetails, IProdOrderLineReportedQuantityDTO, PoCopyPreviewDataResponse } from '@app/core/models/prod-order';
import { EntityRelationDirection, IEntity, IEntityRelation } from '@app/core/services/http-services/model/entity.service';
import { NewProdOrderInputDTO } from '@app/core/models/new-prod-order-input-dto';
import { CreateMultiStepData, CreateMultiStepProdOrderComponent } from '@app/features/operative/controls/create-multi-step-prod-order/create-multi-step-prod-order.component';
import { Guid } from 'guid-typescript';
import { Router } from '@angular/router';
import { CreateMultiStepProdOrderComponentV2 } from '@app-module-operative/controls/create-multi-step-prod-order-v2/create-multi-step-prod-order.component-v2';
import { ICreateProderOrderInputCriteriaDTO } from '@app/core/models/prod-order-create-input.model';
import { ICopyPOPreviewParentPO } from '@app/core/models/operative/prod-order/copy-po-preview/copy-po-preview-parent-po.model';

_('Operative.ProdOrderSchedulerDateOverlappingError');

@Injectable({
  providedIn: 'root',
})
export class ProdOrderService {

  private readonly http = inject(HttpClient);
  private readonly unitConversionService = inject(UnitConversionService);
  private readonly dialogService = inject(DialogService);
  private readonly router = inject(Router);

  constructor() {}

  getProdOrder(id: number): Observable<IProdOrderDTO> {
    return this.http.get<IProdOrderDTO>(`${environment.apiUrl}operative/prod-order/${id}`);
  }

  getProdOrderCopyPreviewData(id: number): Observable<PoCopyPreviewDataResponse> {
    return this.http.get<PoCopyPreviewDataResponse>(`${environment.apiUrl}operative/prod-order/copy-preview/${id}`);
  }

  getProdOrderStates(prodOrderId: number = null, isNew = false, isProdOrderReport = false): Observable<IProdOrderStateDTO[]> {
    let params = new HttpParams();

    if (prodOrderId) {
      params = params.append('prodOrderId', prodOrderId);
    }

    if (isNew) {
      params = params.append('isNew', true);
    } else {
      params = params.append('isNew', false);
    }

    params = params.append('isProdOrderReport', isProdOrderReport);

    return this.http.get<IProdOrderStateDTO[]>(`${environment.apiUrl}operative/prod-order/prod-order-states`, { params });
  }

  createAndSaveProdOrder(prodOrder: ICreateProderOrderInputCriteriaDTO): Observable<number> {
    return this.http.post<number>(`${environment.apiUrl}operative/prod-order/save`, prodOrder);
  }

  copy(id: number): Observable<number> {
    return this.http.post<number>(`${environment.apiUrl}operative/prod-order/copy/${id}`, null);
  }

  // This function is for prod order detail page to calculate Qty of each ProdOrder line.
  // This method will call when Main ProdOrderLine qty changes and ActivityRelationLocked = True
  calculateProdOrderLineQuantity(inputQty: number, prodOrderLines: IProdOrderLineDTO[]): IProdOrderLineDTO[] {
    const isMainProdOrderLineRel = prodOrderLines.find(x => x.mainReportingLine);

    inputQty = inputQty < 0 ? -1 * inputQty : inputQty;
    if (isMainProdOrderLineRel.material.baseUnitCode !== isMainProdOrderLineRel.planedQtyUOM) {
      inputQty = this.getConvertedInput(isMainProdOrderLineRel.material, isMainProdOrderLineRel.material.baseUnitCode, isMainProdOrderLineRel.planedQtyUOM, inputQty);
    }
    // Note - Same logic is used to calculate Qty  of each ProdOrder line while Creating ProdOrder deatail on ProdOrder.cs constructor
    const activityFactor = (isMainProdOrderLineRel.direction === EntityRelationDirection.CONSUMES ? -1 : 1) * (inputQty / isMainProdOrderLineRel.materialRelationSum);
    prodOrderLines.forEach(d => {
      if (d?.activity?.index && !d.mainReportingLine) {
        d.planedQty = Number((activityFactor * d.materialRelationSum).toFixed(4));
      }
    });

    return prodOrderLines;
  }

  calculateDuration(activityIndex: number, materialIndex: number, qty: number, qtyUnit: string = 'M3'): Observable<number> {
    const params = new HttpParams()
      .set('activityIndex', activityIndex.toString())
      .set('materialIndex', materialIndex.toString())
      .set('qty', (qty ?? '0').toString())
      .set('qtyUnit', qtyUnit);

    return this.http.get<number>(`${environment.apiUrl}operative/prod-order/calculate-duration`, { params });
  }

  get(mashineId: number, skip: number = 0, take: number = 20) {
    const params = new HttpParams().set('mashineId', mashineId.toString()).set('skip', skip.toString()).set('take', take.toString());

    return this.http.get<GridDataResult>(`${environment.apiUrl}operative/prod-order`, { params });
  }

  saveProdOrder(prodOrder: IProdOrderDTO): Observable<number> {
    return this.http.post<number>(`${environment.apiUrl}operative/prod-order`, prodOrder);
  }

  copySequence(id: number) {
    return this.http.post<void>(`${environment.apiUrl}operative/prod-order/copy-chain/${id}`, null);
  }

  updateProdOrder(prodOrder: IProdOrderDTO): Observable<boolean> {
    return this.http.put<boolean>(`${environment.apiUrl}operative/prod-order`, prodOrder);
  }

  updateProdOrderOnStateChange(prodOrder: IProdOrderDTO): Observable<any> {
    return this.http.put(`${environment.apiUrl}operative/prod-order/prod-order-state`, prodOrder);
  }

  rescheduleProdOrder(prodOrder: IProdOrderDTO): Observable<number[]> {
    return this.http.put<number[]>(`${environment.apiUrl}operative/prod-order/reschedule-prod-order`, prodOrder);
  }

  removeProdOrder(prodOrderId: number, removeAllInSequence: boolean): Observable<IProdOrderDeleteResponseDTO> {
    return this.http.delete<IProdOrderDeleteResponseDTO>(`${environment.apiUrl}operative/prod-order/${prodOrderId}`, {
      params: new HttpParams().set('removeAllInSequence', removeAllInSequence),
    });
  }

  validateQuantityAsPerDirection(qty: number, direction: number): number {
    if ((direction === EntityRelationDirection.CONSUMES && qty > 0) || (direction === EntityRelationDirection.PRODUCE && qty < 0)) {
      return qty * -1;
    }
    return qty;
  }

  static GetActivityText(line: IProdOrderLineDTO): string {
    return line?.activity !== undefined ? line?.activity?.description : '';
  }

  // this function is used to show planned qty in prodorder create popup
  // This function is same as we used in prod order detail page to calculate Qty of each ProdOrder line but here we used entityrelation
  // instead of prodorderlines.
  calculateEntityRelationQuantity(inputQty: number, entityrelations: IEntityRelation[], product: IEntity, updatedUOM: string = null): IEntityRelation[] {
    const isMainProdOrderLineRel = this.getMainPOLine(entityrelations, product);
    const convertedInputQty = this.getQtyOnBaseQtyChange(inputQty, entityrelations, product, updatedUOM);
    const activityFactor = (isMainProdOrderLineRel.direction === EntityRelationDirection.CONSUMES ? -1 : 1) * (convertedInputQty / isMainProdOrderLineRel.relation);
    return this.getEntityRelations(entityrelations, activityFactor);
  }

  getQtyOnBaseQtyChange(inputQty: number, entityrelations: IEntityRelation[], product: IEntity, updatedUOM: string = null): number {
    const isMainProdOrderLineRel = this.getMainPOLine(entityrelations, product);
    let convertedInputQty = 0;
    inputQty = inputQty < 0 ? -1 * inputQty : inputQty;
    if (updatedUOM != null) {
      convertedInputQty = this.getConvertedInput(product, isMainProdOrderLineRel.unitCode, updatedUOM, inputQty);
    } else {
      convertedInputQty = this.getConvertedInput(product, isMainProdOrderLineRel.unitCode, product.baseUnitCode, inputQty);
    }
    return convertedInputQty;
  }

  getPOShiftStartTime(machineId: number, date: Date): Observable<string> {
    const params = new HttpParams().set('machineId', machineId).set('date', date.toDateString());
    return this.http.get<string>(`${environment.apiUrl}operative/prod-order/po-shift-start-time`, { params });
  }

  getPOLinesWithReportedQty(prodOrderId: number): Observable<IProdOrderLineReportedQuantityDTO[]> {
    return this.http.get<IProdOrderLineReportedQuantityDTO[]>(`${environment.apiUrl}ProdOrderLine/getPOLines/${prodOrderId}`);
  }

  getConvertedInput(product: IEntity, fromUOM: string, toUOM: string, inputQty: number) {
    return this.unitConversionService.qtyConversionByUOM(product, fromUOM, toUOM, inputQty, inputQty);
  }

  getEntityRelations(entityrelations: IEntityRelation[], activityFactor: number): IEntityRelation[] {
    entityrelations.forEach(d => {
      if (d.glulamSpec === null || d.glulamSpec.length === 0) {
        d.plannedQty = Number((activityFactor * d.relation).toFixed(3));
      } else {
        let sumlamellas: number = 0;
        d.glulamSpec.forEach(x => {
          sumlamellas += x.numberOfLammellas;
        });
        d.plannedQty = Number((activityFactor * sumlamellas * -1).toFixed(3));
      }
    });
    return entityrelations;
  }

  bulkDelete(prodOrderIds: number[]): Observable<IProdOrderDeleteResponseDTO[]> {
    let params = new HttpParams();
    prodOrderIds.forEach(element => {
      params = params.append('prodOrderIds', element?.toString());
    });
    return this.http.delete<IProdOrderDeleteResponseDTO[]>(`${environment.apiUrl}operative/prod-order`, { params });
  }

  bulkUpdate(prodOrderBatchInput: IProdOrderBatchDTO): Observable<number> {
    return this.http.put<number>(`${environment.apiUrl}operative/prod-order/bulk-update`, prodOrderBatchInput);
  }

  getActivityForMaterials(materialIndexes: Set<number>, searchDirection: EntityRelationDirection, prodOrderId: number | null): Observable<ProductActivityDetails[]> {
    let params = new HttpParams();
    materialIndexes.forEach(element => {
      params = params.append('materialIndexes', element.toString());
    });
    params = params.append('searchDirection', searchDirection);

    if (prodOrderId != null) {
      params = params.append('prodOrderId', prodOrderId);
    }

    return this.http.get<ProductActivityDetails[]>(`${environment.apiUrl}operative/prod-order-chain/activities`, {
      params,
    });
  }

  openDialogForMultiStepPO(siteId: Guid, prodOrder: NewProdOrderInputDTO, entityRelations: IEntityRelation[], isEmptyStock: boolean, copyPOPreviewParentPOInfo?: ICopyPOPreviewParentPO): DialogRef {
    const dialogRef = this.dialogService.open({
      content: CreateMultiStepProdOrderComponent,
      width: 1000,
      height: 'auto',
      themeColor: 'primary',
    });

    const content = dialogRef.content.instance as CreateMultiStepProdOrderComponent;
    content.data = <CreateMultiStepData>{
      siteId,
      prodOrder,
      entityRelations,
      isEmptyStock,
      copyPOPreviewParentPOInfo: copyPOPreviewParentPOInfo,
    };

    return dialogRef;
  }

  openDialogForMultiStepPOV2(siteId: Guid, prodOrder: NewProdOrderInputDTO): DialogRef {
    const dialogRef = this.dialogService.open({
      content: CreateMultiStepProdOrderComponentV2,
      width: '1800px',
      height: '1000px',
      title: 'Multi Level Forcast',
      actionsLayout: 'end',
      themeColor: 'primary',
      actions: [{ text: 'Close', error: true, action: () => dialogRef.close() }],
    });

    const content = dialogRef.content.instance as CreateMultiStepProdOrderComponentV2;

    content.rootActivityIndex = prodOrder.activityIndex;
    content.selectedDate = prodOrder.endDate;
    content.siteId = siteId;

    content.start();

    return dialogRef;
  }

  openDialogForMultiStepExistingPO(prodOrderId: number, entityRelations: IEntityRelation[]): DialogRef {
    const dialogRef = this.dialogService.open({
      content: CreateMultiStepProdOrderComponent,
      width: 1000,
      height: 'auto',
      themeColor: 'primary',
    });

    const content = dialogRef.content.instance as CreateMultiStepProdOrderComponent;
    content.data = <CreateMultiStepData>{
      prodOrderId,
      entityRelations,
    };

    return dialogRef;
  }

  navigateToProdOrderGrid() {
    this.router.navigate(['/operative/prod-order']);
  }

  private getMainPOLine(entityrelations: IEntityRelation[], product: IEntity) {
    return entityrelations.find(x => x.productIndex === product.index);
  }
}
